import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  ApplicationInformation,
  AwNavigationMenuComponent,
  AwToastComponent,
  AwTopNavigationComponent,
  NavigationMenu,
  NavigationOptions,
  NavigationThemeLabel,
  SideDrawerProfileData,
  SingleSelectOption,
  SiteTheme,
  ToastVariants,
  TopNavigationOptions,
  UserInformation,
} from '@assetworks-llc/aw-component-lib';
import { ThemeService } from '../../core/services/theme.service';
import {
  Organization,
  UserInfo,
  UserService,
} from '../../core/services/user.service';
import { IntegrationService } from '../../core/services/integration.service';
import { ParentIntegrationService } from '../../core/services/parent-integration.service';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'app-main-layout',
  imports: [
    RouterOutlet,
    AwNavigationMenuComponent,
    AwTopNavigationComponent,
    AwToastComponent,
    CommonModule,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  standalone: true,
})
export class MainLayoutComponent implements OnInit {
  theme = signal<string>('light-theme');

  constructor(
    private themeService: ThemeService,
    private userService: UserService,
    private integrationService: IntegrationService,
    private parentIntegrationService: ParentIntegrationService,
    private router: Router
  ) {}
  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  user: UserInfo | null = null;
  organization: Organization | null = null;

  userInformation = signal<UserInformation>({
    userName: 'N/A',
  });
  sideDrawerExpansions = signal<SideDrawerProfileData[]>([]);

  truncateId(str: string | undefined, maxLength: number): string {
    if (str) {
      return str.length > maxLength ? str.slice(0, maxLength) + ' . . .' : str;
    }
    return 'Not Found';
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  ngOnInit() {
    const storedTheme: string = this.themeService.getTheme();
    this.theme.set(storedTheme);

    this.user = this.userService.getUserInfo();
    this.organization = this.userService.getOrganization();

    // Build menu based on organization type
    if (this.organization === 'integration-employee' && this.user?.organizationId) {
      this.parentIntegrationService
        .getParentIntegrationById({ organizationId: this.user.organizationId })
        .pipe(catchError(() => of(null)))
        .subscribe((parentIntegration) => {
          this.buildMenuItems(parentIntegration?.organizationId || null);
        });
    } else {
      this.buildMenuItems(null);
    }

    this.userInformation.set({
      isAdmin: false,
      userName: this.user?.name ?? 'Not Found',
      userId: this.truncateId(this.user?.uuid, 30),
      additionalInfo: [
        {
          label: 'User ID',
          value: this.user?.uuid ?? 'Not Found',
        },
      ],
      userActions: [
        {
          label: 'Log out',
          icon: 'logout-variant',
          action: () => {
            const organizationId: string = this.user?.organizationId || '';
            const urlTree = this.router.createUrlTree(['/logout'], {
              queryParams: {
                reason: 'Logout Requested',
                organizationId: organizationId,
              },
            });
            this.router.navigateByUrl(urlTree, { replaceUrl: true });
          },
        },
      ],
    });

    if (this.organization === 'assetworks-employee') {
      this.sideDrawerExpansions.set([
        {
          expansionTitle: 'User',
          actionsList: [
            {
              label: 'User Name / Email',
              readonly: {
                title: this.user?.name ?? 'Not Found',
                subTitle: this.user?.email,
              },
            },
            {
              label: 'Organization Name / ID',
              readonly: {
                title: 'AssetWorks',
                subTitle: this.user?.organizationId,
              },
            },
            {
              label: 'Theme',
              selectOptions: {
                value: this.themeService.getThemeLabel(),
                action: (value: { label: string }) => {
                  const newTheme: string =
                    this.themeService.buildThemeClassName(value.label);

                  // Set the theme and save to local storage (avoids theme reset after page refresh)
                  this.theme.set(newTheme);
                  this.themeService.setTheme(newTheme);
                },
                singleSelectOptions: [
                  { label: SiteTheme.LightTheme },
                  { label: SiteTheme.DarkTheme },
                ],
              },
            },
          ],
        },
      ]);
    } else if (this.organization === 'integration-employee') {
      this.integrationService
        .getIntegrationById({
          organizationId: this.user?.organizationId ?? '',
        })
        .pipe(catchError((error) => of(null)))
        .subscribe((integration) => {
          this.sideDrawerExpansions.set([
            {
              expansionTitle: 'User',
              actionsList: [
                {
                  label: 'User Name / Email',
                  readonly: {
                    title: this.user?.name ?? 'Not Found',
                    subTitle: this.user?.email,
                  },
                },
                {
                  label: 'Organization Name / ID',
                  readonly: {
                    title: integration?.organizationName ?? 'Not Available',
                    subTitle: this.user?.organizationId,
                  },
                },
                {
                  label: 'Theme',
                  selectOptions: {
                    value: this.themeService.getThemeLabel(),
                    action: (value: { label: string }) => {
                      const newTheme: string =
                        this.themeService.buildThemeClassName(value.label);

                      // Set the theme and save to local storage (avoids theme reset after page refresh)
                      this.theme.set(newTheme);
                      this.themeService.setTheme(newTheme);
                    },
                    singleSelectOptions: [
                      { label: SiteTheme.LightTheme },
                      { label: SiteTheme.DarkTheme },
                    ],
                  },
                },
              ],
            },
          ]);
        });
    }
  }

  // TOP NAV Config
  topNavOptions: TopNavigationOptions = {
    locationFilter: false,
    profileBarColor: NavigationThemeLabel.Black,
    enableSwitchTargetButton: false,
  };

  topNavSelectionOptions: SingleSelectOption[] = [];

  // SIDEBAR NAV CONFIG
  navigationOptions: NavigationOptions = {
    defaultHomePage: '/dashboard',
    enableAwLogo: true,
    onLogoClick: () => {
      window.location.pathname = '/dashboard';
    },
  };

  menuItems: NavigationMenu = { menu: [] };

  private buildMenuItems(parentIntegrationId: string | null): void {
    const menu = [
      {
        title: 'Admin Dashboard',
        link: '/dashboard',
      },
      {
        title: 'Customer Management',
        link: '/customer-management/overview',
      },
    ];

    if (this.organization === 'assetworks-employee') {
      menu.push({
        title: 'Parent Integration Management',
        link: '/parent-integration-management/overview',
      });
      menu.push({
        title: 'Integration Partner Management',
        link: '/integration-management/overview',
      });
    } else if (this.organization === 'integration-employee') {
      if (parentIntegrationId) {
        menu.push({
          title: 'Parent Integration Management',
          link: `/parent-integration-management/parent-integration-details/${parentIntegrationId}`,
        });
        menu.push({
          title: 'Integration Partner Management',
          link: '/integration-management/overview',
        });
      } else {
        menu.push({
          title: 'Integration Partner Management',
          link: `/integration-management/integration-details/${this.user?.organizationId}`,
        });
      }
    }

    menu.push({
      title: 'Employee Management',
      link: '/employee-management/overview',
    });

    this.menuItems = { menu };
  }

  // Made up values for now
  applicationInformation: ApplicationInformation = {
    version: {
      value: '1.0.0',
    },
    enableGmtOffset: true,
  };

  sessionTimerConfig = {
    displayTimer: false,
    timerCountdown: 0,
  };
}
